import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ContextualMenuService } from '../../services/contextual-menu.service';
import { CompaniesService } from '../../services/companies.service';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CompanyUserService } from '../../services/company-user.service';
import constants from 'src/app/shared/constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ConfirmActionDialogNewComponent } from 'src/app/shared/components/confirm-action-dialog-new-style/confirm-action-dialog.component';
import { formatPhoneNumber } from 'src/app/shared/utils/phone-mask.util';
import { Steps } from '../../constants';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ReceiversService } from 'src/app/shared/services/receivers.service';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-company-info',
  templateUrl: './company-info.component.html',
  styleUrls: ['./company-info.component.scss'],
})
export class CompanyInfoComponent implements OnInit, OnDestroy {
  mask: string = '(+00) 0000000000000';
  tabs = [Steps.INFO, Steps.USERS, Steps.SETTINGS];
  filterValue = '';
  companyNames = [];
  companiesSubscription: Subscription;
  filterControl = new FormControl('');

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly domSanitizer: DomSanitizer,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly translateService: TranslateService,
    private readonly companyUserService: CompanyUserService,
    public contextualMenuService: ContextualMenuService,
    public companiesService: CompaniesService,
    public authService: AuthService,
    public dialog: MatDialog,
    public receiversService: ReceiversService
  ) {}

  ngOnInit(): void {
    this.matIconRegistry.addSvgIcon(
      'delete_icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/delete.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'edit_square',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/editsquare.svg')
    );

    this.companiesSubscription = this.companiesService.companies$.subscribe((companies) => {
      this.companyNames = companies.map((company) => company.name).sort();
    });
  }

  roleAllowed() {
    return this.authService.hasRolesAdminOrDistributor();
  }

  convertIp(ip: string) {
    const receiverServer = this.receiversService.receiverServers.find((s) => s.ip === ip);
    if (!receiverServer) return '-';

    return receiverServer.name + ' - ' + receiverServer.location;
  }

  adminHasChosenCompany() {
    return this.companyUserService.adminHasChosenCompany();
  }

  filterWhenTyping(companyNames: string[]) {
    if (this.filterValue.length < 2) {
      return;
    }

    return companyNames.filter((companyName) => companyName.toLowerCase().includes(this.filterValue.toLowerCase()));
  }

  ngOnDestroy(): void {
    this.companiesService.setCurrentTab(Steps.INFO);
    this.companiesSubscription.unsubscribe();
  }

  applyFilter(filterValue: string) {
    this.filterValue = filterValue;
  }

  onCompanySelect(companyName: string) {
    this.filterValue = companyName;
  }

  deleteCompany(companyID) {
    if (this.authService.shouldBlockViewer()) return;
    const company = this.companiesService.getCompany(companyID);
    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        title: this.translateService.instant('Settings.ExcludeCompanyRequest').replace('{{companyName}}', company.name),
        description: 'Settings.ExcludeCompanyRequest_desc',
        rightButton: {
          title: 'Settings.ExcludeCompanyRequest_confirm',
          color: {
            background: 'red',
            font: 'white',
          },
        },
        leftButton: {
          title: 'Shared.Cancel',
          color: {
            background: 'white',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {
      if (result?.status === 'right') this.onSendDelete(companyID);
    });
  }

  isKaironEnabled(company: any) {
    return this.authService.isFeatureFlagEnabled(constants.featureFlags.iaKairon, company._id);
  }

  showSnackBarError = (message: string) => {
    this.snackBar.open(
      `${this.translateService.instant('Shared.Error')}: ${message}`,
      this.translateService.instant('Shared.Close'),
      { duration: 5000 }
    );
  };

  onSendDelete(companyID) {
    this.companyUserService.deleteCompany(companyID).subscribe({
      next: (res: any) => {
        this.onSuccessDelete(companyID);
      },
      error: (error) => {
        if (error?.status === 401) {
          this.snackBar.open(
            this.translateService.instant('Shared.SessionExpired'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
          this.authService.logout();
        } else if (error.error?.reason) {
          this.showSnackBarError(error.error.reason);
        } else {
          this.snackBar.open(
            this.translateService.instant('Settings.Error.NoGetCompany'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
        }
      },
    });
  }

  onSuccessDelete(companyID) {
    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        title: 'Settings.ExcludeCompanySuccess',
        rightButton: {
          title: 'Ok',
          color: {
            background: '#FFCC00',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {
      this.companiesService.removeCompany(companyID);
      this.companiesService.setRefetchCompanies(true);
    });
  }

  editCompany(company) {
    if (this.authService.shouldBlockViewer()) return;
    this.companiesService.onEdit(company, this.isKaironEnabled(company));
  }

  onExpand(item, value) {
    item.expand = value;
  }

  isLast(item, array) {
    return array[array.length - 1] === item;
  }

  getFormattedWhatsappNumber(value) {
    if (!value) return '';
    return formatPhoneNumber(value);
  }

  companiesLogoErrorState = {};
  handleImageError(companyID: string) {
    this.companiesLogoErrorState[companyID] = true;
  }

  onTabChange(event: MatTabChangeEvent) {
    this.companiesService.setCurrentTab(this.tabs[event.index]);
  }
}
